import logoAvif from '../assets/optimized/bestbeighbor2.avif';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, ClockIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import logo from '../assets/optimized/bestbeighbor2.webp';
import { useContent } from '../context/ContentContext';

const quickLinks = [
  { label: 'Home', href: '/', isRoute: true },
  { label: 'About Us', href: '/#about', isRoute: false },
  { label: 'Services', href: '/#services', isRoute: false },
  { label: 'Service Areas', href: '/service-areas', isRoute: true },
  { label: 'Membership Plans', href: '/plans', isRoute: true },
  { label: 'Financing', href: '/financing', isRoute: true },
  { label: 'Contact Us', href: '/contact', isRoute: true },
];

const services = [
  { label: 'AC Installation & Repair', index: 0 },
  { label: 'Heating Services', index: 1 },
  { label: 'Heat Pump Solutions', index: 2 },
  { label: 'Mini Split Systems', index: 3 },
  { label: 'Preventive Maintenance', index: 4 },
  { label: 'Emergency HVAC Service', index: 5 },
];

export default function Footer() {
  const { settings } = useContent();
  const phoneDigits = settings.phone.replace(/[^0-9]/g, '');

  return (
    <footer id="contact" className="relative overflow-hidden" style={{ backgroundColor: '#111b21' }}>
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-orange/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-orange/5 rounded-full blur-3xl" />
      </div>

      <div className="relative">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16">
          <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6 sm:p-8 lg:p-12 mb-14 sm:mb-20">
            <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-5">
                  <span className="w-2 h-2 bg-brand-orange rounded-full" />
                  <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                    Contact Us
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-white mb-3">
                  Ready for Reliable Comfort?
                </h2>
                <p className="text-white/50 leading-relaxed max-w-lg text-sm sm:text-base">
                  Get a free, no-obligation estimate from North Texas's trusted
                  veteran-owned HVAC team. Same-day appointments available.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 sm:gap-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-brand-orange text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25"
                >
                  <span>Get Your Free Estimate</span>
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={`tel:${phoneDigits}`}
                  className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold hover:border-brand-orange hover:text-brand-orange transition-all duration-300"
                >
                  <PhoneIcon className="w-5 h-5" />
                  <span>{settings.phone}</span>
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-8">
            <div className="sm:col-span-2 lg:col-span-4">
              <picture><source type="image/avif" srcSet={logoAvif} /><img decoding="async" loading="lazy" width={600} height={173} src={logo} alt="Best Neighbor Home Services" className="h-14 sm:h-16 w-auto mb-5 sm:mb-6" /></picture>
              <p className="text-white/50 leading-relaxed mb-6 sm:mb-8 max-w-sm text-sm sm:text-base">
                Veteran-owned HVAC services delivering comfort, safety, and peace of mind to
                North Texas families since day one.
              </p>
              <div className="flex gap-3">
                <a
                  href="https://facebook.com/bestneighborhomeservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center hover:bg-brand-orange hover:border-brand-orange transition-all duration-300"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 320 512" aria-hidden="true">
                    <path d="M80 299.3V512H196V299.3h86.5l18-97.8H196V142.5c0-27.2 7.5-45.6 46.4-45.6H302V6.3C293.5 5.2 265.2 3 232.7 3 165.4 3 119.7 44.5 119.7 126.5V201.5H32v97.8H119.7z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/best_neighbor_home_services/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center hover:bg-brand-orange hover:border-brand-orange transition-all duration-300"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                  </svg>
                </a>
                <a
                  href="https://maps.app.goo.gl/K32NCfZ5MP2JR76d9?g_st=ic"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center hover:bg-brand-orange hover:border-brand-orange transition-all duration-300"
                  aria-label="Google"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h2 className="font-sans !leading-[1.4] text-white font-semibold text-sm uppercase tracking-wider mb-4 sm:mb-6">Quick Links</h2>
              <ul className="space-y-2.5 sm:space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.label}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-white/50 hover:text-brand-orange transition-colors duration-300 text-sm"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-white/50 hover:text-brand-orange transition-colors duration-300 text-sm"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <h2 className="font-sans !leading-[1.4] text-white font-semibold text-sm uppercase tracking-wider mb-4 sm:mb-6">Our Services</h2>
              <ul className="space-y-2.5 sm:space-y-3">
                {services.map((service) => (
                  <li key={service.label}>
                    <a
                      href={`/#services?service=${service.index}`}
                      className="text-white/50 hover:text-brand-orange transition-colors duration-300 text-sm"
                    >
                      {service.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <h2 className="font-sans !leading-[1.4] text-white font-semibold text-sm uppercase tracking-wider mb-4 sm:mb-6">Contact Info</h2>
              <ul className="space-y-3 sm:space-y-4">
                <li>
                  <a
                    href={`tel:${phoneDigits}`}
                    className="flex items-center gap-3 text-white/50 hover:text-brand-orange transition-colors duration-300 text-sm"
                  >
                    <div className="w-9 h-9 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <PhoneIcon className="w-4 h-4 text-brand-orange" />
                    </div>
                    {settings.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${settings.email}`}
                    className="flex items-center gap-3 text-white/50 hover:text-brand-orange transition-colors duration-300 text-sm"
                  >
                    <div className="w-9 h-9 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <EnvelopeIcon className="w-4 h-4 text-brand-orange" />
                    </div>
                    <span className="break-all">{settings.email}</span>
                  </a>
                </li>
                <li className="flex items-start gap-3 text-white/50 text-sm">
                  <div className="w-9 h-9 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPinIcon className="w-4 h-4 text-brand-orange" />
                  </div>
                  <span className="pt-2">414 Metro Park Dr Suite 201<br />McKinney, TX 75071</span>
                </li>
                <li className="flex items-start gap-3 text-white/50 text-sm">
                  <div className="w-9 h-9 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ClockIcon className="w-4 h-4 text-brand-orange" />
                  </div>
                  <span className="pt-2">Monday - Sunday: 24/7<br />Including weekends and holidays</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-white/30 text-xs sm:text-sm">
                2026 Best Neighbor Home Services. All Rights Reserved. Built by <a href="https://www.je.design" target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:text-brand-orange/80 transition-colors duration-300">Jeremy Ellsworth</a>
              </p>
              <div className="flex gap-4 sm:gap-6 text-xs sm:text-sm">
                <Link to="/privacy-policy" className="text-white/30 hover:text-white/60 transition-colors duration-300">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" className="text-white/30 hover:text-white/60 transition-colors duration-300">
                  Terms of Service
                </Link>
                <span className="text-[#75848b]">{settings.license}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
